// Brand theming. The Worker maps the host (my.{brand} / ops.{brand}) to a
// brand id from its config and answers /api/v1/brand for that brand, so one
// build serves every brand. Colours land in CSS variables that the Tailwind
// preset reads.
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Brand, BrandFont } from './api/types.ts';
import { apiFetch } from './api/client.ts';

const BrandContext = createContext<Brand | null>(null);

function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

function luminance([r, g, b]: [number, number, number]) {
  const ch = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

export function contrastRatio(a: string, b: string): number {
  const [x, y] = [hexToRgb(a), hexToRgb(b)];
  if (!x || !y) return 1;
  const [l1, l2] = [luminance(x), luminance(y)].sort((p, q) => q - p) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

/** The brand's typeface, if its config has one (`theme.font`). */
export function brandFont(brand: Brand): BrandFont | null {
  const font = brand.theme.font;
  // Only Google Fonts stylesheets: the dashboards' CSP allows nothing else.
  if (!font?.family || !font.cssUrl?.startsWith('https://fonts.googleapis.com/css2?')) return null;
  return { family: font.family, cssUrl: font.cssUrl };
}

function applyHeadingFont(font: BrandFont | null | undefined) {
  const id = 'brand-heading-font';
  document.getElementById(id)?.remove();
  if (!font?.family || !font.cssUrl?.startsWith('https://fonts.googleapis.com/css2?')) {
    document.documentElement.style.removeProperty('--brand-heading-font');
    return;
  }
  document.head.appendChild(Object.assign(document.createElement('link'), { id, rel: 'stylesheet', href: font.cssUrl }));
  document.documentElement.style.setProperty('--brand-heading-font', `"${font.family.replace(/"/g, '')}", var(--brand-font)`);
}

function applyFont(font: BrandFont | null) {
  const id = 'brand-font';
  document.getElementById(id)?.remove();
  if (!font) {
    document.documentElement.style.removeProperty('--brand-font');
    return;
  }
  const link = Object.assign(document.createElement('link'), { id, rel: 'stylesheet', href: font.cssUrl });
  document.head.appendChild(link);
  document.documentElement.style.setProperty('--brand-font', `"${font.family.replace(/"/g, '')}", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`);
}

const PALETTE_KEYS = ['canvas', 'surface', 'ink', 'mid', 'muted', 'line'] as const;

/**
 * Brand colours, fonts and (for customer-facing apps) the brand's neutrals.
 * The ops console keeps its own plain greys, so it passes `palette: false`.
 */
export function applyTheme(brand: Brand, { palette = true }: { palette?: boolean } = {}) {
  const root = document.documentElement;
  const primary = hexToRgb(brand.theme.primary);
  let on = brand.theme.onPrimary;
  // Keep text on brand colour readable (WCAG AA) even if a brand's config isn't.
  if (contrastRatio(brand.theme.primary, on) < 4.5) {
    const alt = contrastRatio(brand.theme.primary, '#ffffff') >= contrastRatio(brand.theme.primary, '#111827') ? '#ffffff' : '#111827';
    console.warn(`[brand] ${brand.id}: onPrimary ${on} on ${brand.theme.primary} is below 4.5:1; using ${alt}`);
    on = alt;
  }
  if (primary) root.style.setProperty('--brand', primary.join(' '));
  root.style.setProperty('--on-brand', hexToRgb(on)!.join(' '));
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', brand.theme.primary);
  if (brand.theme.markUrl?.startsWith('https://')) document.querySelector('link[rel="icon"]')?.setAttribute('href', brand.theme.markUrl);
  applyFont(brandFont(brand));
  applyHeadingFont(brand.theme.headingFont);
  const accent = hexToRgb(brand.theme.accent ?? '');
  if (accent) root.style.setProperty('--accent', accent.join(' '));
  else root.style.removeProperty('--accent');
  for (const k of PALETTE_KEYS) {
    const rgb = palette ? hexToRgb(brand.theme.palette?.[k] ?? '') : null;
    if (rgb) root.style.setProperty(`--${k}`, rgb.join(' '));
    else root.style.removeProperty(`--${k}`);
  }
}

/**
 * The brand's logo, or its name as a wordmark when it has none. `onBrand` is
 * for a coloured header: the logo sits on a white chip so any logo stays legible.
 */
export function BrandMark({ className, onBrand, suffix }: { className?: string; onBrand?: boolean; suffix?: string }) {
  const brand = useBrand();
  const logo = brand.theme.logoUrl;
  if (!logo)
    return (
      <span className={`inline-flex items-center gap-2 ${className ?? ''}`}>
        <span
          aria-hidden
          className={
            onBrand
              ? 'flex h-8 w-8 items-center justify-center rounded-xl bg-white text-base font-bold text-brand'
              : 'flex h-8 w-8 items-center justify-center rounded-xl bg-brand text-base font-bold text-on-brand'
          }
        >
          {brand.name.slice(0, 1)}
        </span>
        <span className={onBrand ? 'text-lg font-bold tracking-tight' : 'text-lg font-bold tracking-tight text-ink'}>{brand.name}</span>
        {suffix && <span className={onBrand ? 'rounded-md bg-white/15 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide' : 'rounded-md bg-surface px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-mid'}>{suffix}</span>}
      </span>
    );
  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ''}`}>
      <span className={onBrand ? 'inline-flex rounded-md bg-white px-2 py-1' : 'inline-flex'}>
        <img src={logo} alt={brand.name} className="h-7 w-auto max-w-[160px] object-contain" />
      </span>
      {suffix && <span className={onBrand ? 'font-semibold' : 'font-semibold text-mid'}>{suffix}</span>}
    </span>
  );
}

/** `brandId` is for the ops console, where staff switch brand; the customer app lets the Worker pick from the host. */
export function BrandProvider({ children, titleSuffix, fallback, brandId, palette = true }: { children: ReactNode; titleSuffix?: string; fallback: ReactNode; brandId?: string; palette?: boolean }) {
  const { data, error } = useQuery({
    queryKey: ['brand', brandId ?? 'host'],
    queryFn: () => apiFetch<Brand>('/brand', {}, brandId ? { 'X-Brand': brandId } : {}),
    staleTime: Infinity,
    placeholderData: (prev) => prev,
  });
  useEffect(() => {
    if (!data) return;
    applyTheme(data, { palette });
    document.title = titleSuffix ? `${data.name} ${titleSuffix}` : data.name;
  }, [data, titleSuffix, palette]);
  if (error) return <div role="alert" className="p-6">We couldn’t load this site’s settings. Please refresh in a minute.</div>;
  if (!data) return <>{fallback}</>;
  return <BrandContext.Provider value={data}>{children}</BrandContext.Provider>;
}

export function useBrand(): Brand {
  const brand = useContext(BrandContext);
  if (!brand) throw new Error('useBrand outside BrandProvider');
  return brand;
}

/** wa.me link to the brand's WhatsApp number, optionally with pre-typed text. */
export function whatsappLink(brand: Brand, text?: string): string | null {
  const n = brand.contact.whatsapp?.replace(/[^\d]/g, '');
  if (!n) return null;
  return `https://wa.me/${n}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}
